/**
 * AuthorshipPanel - Painel de Participação dos Autores
 * 
 * Exibe a contribuição de cada autor em um livro colaborativo
 * com visualização de palavras adicionadas/removidas e porcentagem
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  PenTool, 
  TrendingUp, 
  TrendingDown,
  Crown,
  Award,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthorshipPanel({ bookId, bookTitle }) {
  const [participation, setParticipation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (bookId) {
      fetchParticipation();
    }
  }, [bookId]);

  const fetchParticipation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/coautoria/participation/${bookId}`,
        { withCredentials: true }
      );
      setParticipation(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching participation:', err);
      setError('Não foi possível carregar dados de participação');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
        {error}
      </div>
    );
  }

  if (!participation || !participation.authors || participation.authors.length === 0) {
    return (
      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-3 text-gray-400">
          <Users className="h-5 w-5" />
          <span>Nenhum dado de autoria disponível ainda</span>
        </div>
      </div>
    );
  }

  const { authors, total_words } = participation;
  const totalContributions = authors.reduce((sum, a) => sum + Math.abs(a.net_contribution || 0), 0);

  return (
    <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/10 backdrop-blur-sm rounded-xl border border-amber-500/20 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid="authorship-panel-header"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <Users className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Participação dos Autores</h3>
            <p className="text-sm text-gray-400">
              {authors.length} {authors.length === 1 ? 'autor' : 'autores'} • {formatNumber(Math.abs(total_words))} palavras
            </p>
          </div>
        </div>
        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4" data-testid="authorship-panel-content">
          {authors.map((author, index) => {
            const percentage = totalContributions > 0 
              ? Math.round((Math.abs(author.net_contribution || 0) / totalContributions) * 100) 
              : 0;
            const isCreator = !author.invited_by;
            
            return (
              <div 
                key={author.user_id || index}
                className="bg-white/5 rounded-lg p-4 space-y-3"
                data-testid={`author-card-${index}`}
              >
                {/* Author Info */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                        {(author.user_name || 'A').charAt(0).toUpperCase()}
                      </div>
                      {isCreator && (
                        <div className="absolute -top-1 -right-1 p-1 bg-yellow-500 rounded-full">
                          <Crown className="h-3 w-3 text-yellow-900" />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{author.user_name || 'Autor'}</span>
                        {isCreator && (
                          <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                            Criador
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{author.role === 'owner' ? 'Proprietário' : 'Co-autor'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-amber-400">{percentage}%</div>
                    <p className="text-xs text-gray-500">contribuição</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1">
                  <Progress 
                    value={percentage} 
                    className="h-2 bg-white/10"
                  />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-500/10 rounded-lg p-2">
                    <div className="flex items-center justify-center gap-1 text-green-400">
                      <TrendingUp className="h-3 w-3" />
                      <span className="font-semibold">{formatNumber(author.total_words_added || 0)}</span>
                    </div>
                    <p className="text-xs text-gray-500">adicionadas</p>
                  </div>
                  <div className="bg-red-500/10 rounded-lg p-2">
                    <div className="flex items-center justify-center gap-1 text-red-400">
                      <TrendingDown className="h-3 w-3" />
                      <span className="font-semibold">{formatNumber(author.total_words_removed || 0)}</span>
                    </div>
                    <p className="text-xs text-gray-500">removidas</p>
                  </div>
                  <div className="bg-amber-500/10 rounded-lg p-2">
                    <div className="flex items-center justify-center gap-1 text-amber-400">
                      <FileText className="h-3 w-3" />
                      <span className="font-semibold">{author.chapters_contributed || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">capítulos</p>
                  </div>
                </div>

                {/* Timeline */}
                {(author.first_contribution || author.last_contribution) && (
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-white/5">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>Primeira: {formatDate(author.first_contribution)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <PenTool className="h-3 w-3" />
                      <span>Última: {formatDate(author.last_contribution)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
